import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "./css/styles.css";
import { createPortal } from "react-dom";
import { BrowserRouter, Routes, Route, useParams } from "react-router-dom";
import tiersConfig from "./config/tiers.json";

// ─────────────────────────────────────────────────────────────────────────────
//  CONFIG — tier keys/multipliers/brand live in src/config/tiers.json, the
//  single source of truth shared with scripts/build-data.js.
// ─────────────────────────────────────────────────────────────────────────────
const CONFIG = {
  title:         tiersConfig.title,
  lastUpdate:    tiersConfig.lastUpdateFallback,
  tiers:         tiersConfig.tiers,
  internalSlug:  tiersConfig.internalSlug,
};
// ─────────────────────────────────────────────────────────────────────────────

const CALL_TIER = { key: null, multiplier: null, callOnly: true };

// Columns that get Excel-style value picker filter
// Keys are matched case-insensitively against header names
const FILTERABLE_COLS = [
  "PRODUCT NAME", "INSTALATION", "DIMENSION", "POWER",
  "OPTIC OPTIONS", "TEMPERATURE", "COLOR", "DIMM", "UPLIGHT"
];
function isFilterable(key) {
  const up = key.trim().toUpperCase();
  return FILTERABLE_COLS.some(f => up === f.toUpperCase());
}
function isSearchFilter(key) {
  return key.trim().toUpperCase().includes("REFERENCE");
}
// Matches "PVP" and "PVP €" (BPM/KOHL use "PVP €", UL's USD list uses plain "PVP").
// Deliberately narrow — other €-columns (RAEE eco-tax, per-variant accessory
// prices) legitimately have blank/zero values that are NOT "ask for price".
function isPvpCol(key) {
  const compact = key.trim().toUpperCase().replace(/\s+/g, "");
  return compact === "PVP" || compact === "PVP€";
}
const ON_REQUEST = "ON REQUEST";
function isOnRequestVal(v) {
  return v === "" || v == null || parseNum(v) === 0;
}


const TIER_COOKIE = "pb_tier";
function saveTierCookie(t) {
  if (!t) { document.cookie = TIER_COOKIE + "=;max-age=0;path=/"; return; }
  const val = t.callOnly ? "__call__" : t.key;
  document.cookie = TIER_COOKIE + "=" + val + ";max-age=" + (60*60*24*365) + ";path=/";
}
function loadTierCookie() {
  const match = document.cookie.split(";").map(c => c.trim()).find(c => c.startsWith(TIER_COOKIE + "="));
  if (!match) return null;
  const val = match.split("=")[1];
  if (val === "__call__") return CALL_TIER;
  return Object.values(CONFIG.tiers).find(t => t.key === val) ?? null;
}
const INTERNAL_TIER = { key: "__internal__", multiplier: null, internal: true };

function detectTierFromSlug(slug) {
  if (!slug) return null;
  if (CONFIG.tiers[slug]) return CONFIG.tiers[slug];
  if (slug === CONFIG.internalSlug) return INTERNAL_TIER;
  return null;
}

const LIST_COOKIE = "pb_list";
function saveListCookie(l) {
  document.cookie = LIST_COOKIE + "=" + l + ";max-age=" + (60*60*24*365) + ";path=/";
}
function loadListCookie() {
  const m = document.cookie.split(";").map(c => c.trim()).find(c => c.startsWith(LIST_COOKIE + "="));
  return m ? m.split("=")[1] : null;
}

// ─── Data loading ────────────────────────────────────────────────────────────
// Prices are pre-computed at build time (scripts/build-data.js) into small
// per-tier JSON files, named with a content hash and listed in manifest.json.
// The browser only ever fetches its own tier's already-final numbers — no XLSX
// parsing and no tariff math happens on the user's computer, and a hashed URL
// is safe to cache forever since it changes whenever the data does.
function dataUrl(path) {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}/data/${path}`;
}

async function fetchManifest() {
  // Always revalidated — it's tiny and is the single source of truth for
  // which hashed file is current, so it must never be served stale.
  const r = await fetch(dataUrl("manifest.json"), { cache: "no-store" });
  if (!r.ok) { const e = new Error("manifest"); e.kind = "notfound"; throw e; }
  return r.json();
}

async function fetchTierRows(manifest, tierKey, list) {
  const entry = manifest?.tiers?.[tierKey]?.[list];
  if (!entry) { const e = new Error("notfound"); e.kind = "notfound"; throw e; }
  const r = await fetch(dataUrl(entry.file));
  if (!r.ok) {
    const e = new Error("fetch:" + r.status);
    e.kind = r.status === 404 ? "notfound" : "network";
    throw e;
  }
  // Wire format is columnar (see scripts/build-data.js) — header names are
  // written once, not once per row. Re-expand into {header: value} objects
  // here, at the fetch boundary, so every consumer downstream is unchanged.
  const { columns, rows: rowArrays } = await r.json();
  const rows = rowArrays.map(vals => {
    const obj = {};
    for (let i = 0; i < columns.length; i++) obj[columns[i]] = vals[i];
    return obj;
  });
  return { rows, generatedAt: entry.generatedAt, file: entry.file };
}

function useAutoLoad(tierKey, list, onData, reloadKey) {
  const [status,      setStatus]     = useState("idle");
  const [errorKind,   setErrorKind]  = useState(null);
  const [generatedAt, setGeneratedAt]= useState(null);
  const [retryTick,   setRetryTick]  = useState(0);

  // Deliberately does NOT depend on `status` — this effect is the only thing
  // that sets it, so including it would make the effect's own state update
  // retrigger its cleanup (cancelling the fetch it just started) on every
  // idle→loading transition. tierKey/list/reloadKey/retryTick are the only
  // real reasons to (re)fetch.
  useEffect(() => {
    if (!tierKey || !list) return;
    let cancelled = false;
    setStatus("loading");
    setErrorKind(null);

    async function attempt(isRetry) {
      try {
        const manifest = await fetchManifest();
        const { rows, generatedAt: gen } = await fetchTierRows(manifest, tierKey, list);
        if (cancelled) return;
        onData(rows);
        setGeneratedAt(gen);
        setStatus("done");
      } catch (err) {
        if (cancelled) return;
        if (!isRetry) {
          // Transient network hiccups are common on a first load after a cache
          // clear — one silent retry before we show an error state.
          await new Promise(r => setTimeout(r, 800));
          if (cancelled) return;
          return attempt(true);
        }
        setErrorKind(err.kind === "notfound" ? "notfound" : "network");
        setStatus("error");
      }
    }
    attempt(false);
    return () => { cancelled = true; };
  }, [tierKey, list, reloadKey, retryTick]); // eslint-disable-line react-hooks/exhaustive-deps

  function refresh() { setRetryTick(t => t + 1); }

  return { status, errorKind, refresh, generatedAt };
}


const THEME_COOKIE = "pb_theme";
function saveThemeCookie(t) {
  document.cookie = THEME_COOKIE + "=" + t + ";max-age=" + (60*60*24*365) + ";path=/";
}
function loadThemeCookie() {
  const m = document.cookie.split(";").map(c => c.trim()).find(c => c.startsWith(THEME_COOKIE + "="));
  return m ? m.split("=")[1] : "dark";
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function parseNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
  return isNaN(n) ? null : n;
}

// ─── List selector card ──────────────────────────────────────────────────────
function ListCard({ type, label, sub, icon, onClick }) {
  return (
    <div onClick={onClick} className={`pl-card pl-card--${type === "reduced" ? "a" : "b"}`}>
      <div className="pl-card__glow-blob" />
      <div className="pl-card__icon">
        {icon === "▲" ? <svg width="34" height="34" viewBox="0 0 32 32"><polygon points="16,4 30,28 2,28" className="pl-card__icon-shape" /></svg>
         : icon === "◆" ? <svg width="34" height="34" viewBox="0 0 32 32"><polygon points="16,2 30,16 16,30 2,16" className="pl-card__icon-shape" /></svg>
         : icon === "■" ? <svg width="30" height="30" viewBox="0 0 32 32"><rect x="4" y="4" width="24" height="24" rx="4" className="pl-card__icon-shape" /></svg>
         : <svg width="34" height="34" viewBox="0 0 32 32"><circle cx="16" cy="16" r="13" className="pl-card__icon-shape" /></svg>
        }
      </div>
      <div>
        <div className="pl-card__label">{label}</div>
        <div className="pl-card__sub">{sub}</div>
      </div>
      <div className="pl-card__action">SELECT</div>
    </div>
  );
}

// ─── Theme toggle button ─────────────────────────────────────────────────────
function ThemeToggle({ theme, onToggle }) {
  const [hov, setHov] = useState(false);
  const isDark = theme === "dark";
  return (
    <button onClick={onToggle}
      title={isDark ? "Switch to Light" : "Switch to Dark"}
      className="pl-theme-btn">
      {isDark
        ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
        : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
      }
      {isDark ? "Light" : "Dark"}
    </button>
  );
}

// ─── Glass button component ──────────────────────────────────────────────────
function GlassBtn({ children, onClick, isSwitch = false, style = {}, dot = false }) {
  return (
    <button onClick={onClick}
      className={`pl-glass-btn${!isSwitch ? " pl-glass-btn--default" : ""}`}
      style={style}>
      {dot && <span className="pl-glass-btn__dot" />}
      {children}
    </button>
  );
}

// ─── Type filter button (ACC / PRODUCT) ─────────────────────────────────────
function TypeBtn({ label, active, isLastActive, onClick }) {
  return (
    <button
      onClick={!isLastActive ? onClick : undefined}
      className={`pl-type-btn ${active ? "pl-type-btn--active" : "pl-type-btn--inactive"}${isLastActive ? " pl-type-btn--last" : ""}`}>
      {label.toUpperCase()}
    </button>
  );
}

// ─── Reference search filter dropdown (input only) ──────────────────────────
function SearchFilterDropdown({ col, active, onClose, onApply, anchorRect }) {
  const [search, setSearch] = useState(active ? [...active][0] ?? "" : "");
  const ref = useRef(null);
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);
  const top  = anchorRect ? Math.min(anchorRect.bottom + 4, window.innerHeight - 120) : 100;
  const left = anchorRect ? Math.min(anchorRect.left, window.innerWidth - 260) : 100;
  return createPortal(
    <div ref={ref} className="pl-dropdown pl-dropdown--search" style={{ top, left }}>
      <div className="pl-dropdown__search-wrap">
        <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") onApply(search ? new Set([search]) : null); if (e.key === "Escape") onClose(); }}
          placeholder={`Search ${col.label}…`}
          className="pl-dropdown__search" />
      </div>
      <div className="pl-dropdown__actions">
        <button onClick={() => onApply(search ? new Set([search]) : null)} className="pl-dropdown__apply">Apply</button>
        <button onClick={() => { setSearch(""); onApply(null); }} className="pl-dropdown__clear">Clear</button>
      </div>
    </div>,
    document.body
  );
}

// ─── Column filter dropdown ──────────────────────────────────────────────────
function ColFilterDropdown({ col, rows, active, onClose, onApply, anchorRect }) {
  const allVals = useMemo(() => {
    const s = new Set();
    rows.forEach(r => s.add(String(r[col.key] ?? "")));
    return [...s].filter(Boolean).sort();
  }, [rows, col.key]);

  const [selected, setSelected] = useState(() => active ? new Set(active) : new Set(allVals));
  const [search,   setSearch]   = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const visible = search ? allVals.filter(v => v.toLowerCase().includes(search.toLowerCase())) : allVals;
  const allChecked = visible.length > 0 && visible.every(v => selected.has(v));

  function toggle(v) {
    setSelected(prev => { const next = new Set(prev); next.has(v) ? next.delete(v) : next.add(v); return next; });
  }

  function toggleAll() {
    if (allChecked) {
      setSelected(new Set()); // deselect all
    } else {
      setSelected(new Set(allVals)); // select all
    }
  }

  // Position: below the anchor, flip up if near bottom
  const top  = anchorRect ? Math.min(anchorRect.bottom + 4, window.innerHeight - 320) : 100;
  const left = anchorRect ? Math.min(anchorRect.left, window.innerWidth - 260) : 100;

  return createPortal(
<div ref={ref} className="pl-dropdown" style={{ top, left }}>
      {/* Search inside dropdown */}
      <div className="pl-dropdown__search-wrap">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search values…"
          className="pl-dropdown__search" />
      </div>
      {/* Select all */}
<div onClick={toggleAll} className="pl-dropdown__select-all">
        <Checkbox checked={allChecked} />
        <span className="pl-dropdown__select-all-label">SELECT ALL</span>
      </div>
      {/* Values list */}
      <div className="pl-dropdown__list">
        {visible.map(v => (
<div key={v} onClick={() => toggle(v)} className="pl-dropdown__item">
            <Checkbox checked={selected.has(v)} />
            <span className="pl-dropdown__item-label">{v}</span>
          </div>
        ))}
        {visible.length === 0 && (
          <div className="pl-dropdown__empty">No results</div>
        )}
      </div>
      {/* Buttons */}
      <div className="pl-dropdown__actions">
<button onClick={() => onApply(selected.size === allVals.length ? null : selected)} className="pl-dropdown__apply">
          Apply
        </button>
<button onClick={() => { setSelected(new Set(allVals)); onApply(null); }} className="pl-dropdown__clear">
          Clear
        </button>
      </div>
    </div>,
    document.body
  );
}

function Checkbox({ checked }) {
  return (
    <div className={`pl-checkbox${checked ? " pl-checkbox--checked" : ""}`}>
      {checked && <svg width="8" height="8" viewBox="0 0 8 8"><polyline points="1,4 3,6 7,2" fill="none" stroke="currentColor" strokeWidth="1.5"/></svg>}
    </div>
  );
}

// ─── Virtual List ──────────────────────────────────────────────────────────────
const ROW_H    = 42;
const OVERSCAN = 20;

// ─── Root Reference clickable cell ───────────────────────────────────────────
function RootRefCell({ ci, col, val, isCallCell, columns, onCellClick }) {
  const isRootRef = onCellClick && col.key.trim().toUpperCase() === "ROOT REFERENCE";
  return (
    <div
      onClick={isRootRef ? () => onCellClick(val) : undefined}
className={`pl-cell ${isRootRef ? "pl-cell--rootref" : col.isNumeric ? "pl-cell--price" : "pl-cell--text"}`} style={{ width: col.width, minWidth: col.width }}>
      {isCallCell
        ? <span className="pl-get-quote">Get Quote</span>
        : (val ?? "—")}
    </div>
  );
}

function VirtualList({ rows, allRows, columns, onSort, sortCol, sortDir, callOnly, onResize, colFilters, onColFilter, onCellClick, filteredRows }) {
  const ref = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [vpH, setVpH]             = useState(600);
  const [openFilter, setOpenFilter] = useState(null); // col key
  const [filterAnchor, setFilterAnchor] = useState(null); // DOMRect

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setVpH(el.clientHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const onScroll = useCallback(e => setScrollTop(e.currentTarget.scrollTop), []);
  const totalW   = columns.reduce((s, c) => s + c.width, 0);
  const totalH   = rows.length * ROW_H;
  const start    = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
  const end      = Math.min(rows.length, start + Math.ceil(vpH / ROW_H) + OVERSCAN * 2);
  const slice    = rows.slice(start, end);
  const offY     = start * ROW_H;
  const rowsForDropdown = useMemo(() => {
    const base = filteredRows ?? rows;
    if (!openFilter) return base;
    const others = Object.entries(colFilters).filter(([k, v]) => k !== openFilter && v);
    if (others.length === 0) return base;
    return base.filter(row => others.every(([k, allowed]) => {
      const val = String(row[k] ?? "");
      if (isSearchFilter(k)) {
        const term = [...allowed][0]?.toLowerCase() ?? "";
        return !term || val.toLowerCase().includes(term);
      }
      return allowed.has(val);
    }));
  }, [openFilter, filteredRows, rows, colFilters]);

  return (
    <div ref={ref} onScroll={onScroll} className="pl-table-scroll">

      {/* Header */}
      <div className="pl-thead" style={{ minWidth: totalW }}>
        <div className="pl-thead__row">
          {columns.map((col, i) => (
<div key={i} className="pl-th" style={{ width: col.width, minWidth: col.width }}>
              {/* Sort click area */}
<div onClick={() => onSort(col.key)} className={`pl-th__sort${sortCol === col.key ? " pl-th__sort--active" : ""}${col.isNumeric ? " pl-th__sort--numeric" : ""}${isFilterable(col.key) ? " pl-th__sort--filterable" : ""}`}>
                {col.label}
                <span className={`pl-th__sort-icon${sortCol === col.key ? " pl-th__sort-icon--active" : ""}`}>
                  {sortCol === col.key ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
                </span>
              </div>
              {/* Filter icon — for FILTERABLE or REFERENCE cols */}
              {(isFilterable(col.key) || isSearchFilter(col.key)) && (
                <div onClick={e => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setFilterAnchor(r); setOpenFilter(openFilter === col.key ? null : col.key); }}
                  className={`pl-th__filter-icon${colFilters[col.key] ? " pl-th__filter-icon--active" : ""}`}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                    <path d="M1 2h8L6 5.5V9L4 8V5.5L1 2z"/>
                  </svg>
                </div>
              )}
              {/* Dropdown */}
              {openFilter === col.key && isSearchFilter(col.key) && (
                <SearchFilterDropdown
                  col={col}
                  active={colFilters[col.key]}
                  onClose={() => setOpenFilter(null)}
                  onApply={val => { onColFilter(col.key, val); setOpenFilter(null); }}
                  anchorRect={filterAnchor}
                />
              )}
              {openFilter === col.key && isFilterable(col.key) && (
                <ColFilterDropdown
                  col={col}
                  rows={rowsForDropdown}
                  active={colFilters[col.key]}
                  onClose={() => setOpenFilter(null)}
                  onApply={val => { onColFilter(col.key, val); setOpenFilter(null); }}
                  anchorRect={filterAnchor}
                />
              )}
              {/* Resize handle */}
              <div className="pl-th__resize"
                onMouseDown={e => {
                  e.stopPropagation();
                  const startX = e.clientX;
                  const startW = col.width;
                  const onMove = mv => onResize(col.key, startW + mv.clientX - startX);
                  const onUp   = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
                  window.addEventListener("mousemove", onMove);
                  window.addEventListener("mouseup", onUp);
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Rows */}
      <div className="pl-tbody" style={{ minWidth: totalW }}>
        <div className="pl-tbody__sizer" style={{ height: totalH }}>
          <div className="pl-tbody__offset" style={{ transform: `translateY(${offY}px)` }}>
            {slice.map((row, ri) => {
              const abs = start + ri;
              return (
<div key={row.__id ?? abs} className={`pl-row ${abs % 2 === 0 ? "pl-row--even" : "pl-row--odd"}`}>
                  {columns.map((col, ci) => {
                    let val        = row[col.key];
                    let isCallCell = false;
                    if (col.isNumeric) {
                      if (callOnly) {
                        isCallCell = true;
                      } else if (isPvpCol(col.key) && isOnRequestVal(val)) {
                        val = ON_REQUEST;
                      } else if (val !== "" && val != null) {
                        // Value is already this tier's final price (computed at build time) — format only.
                        const n = parseNum(val);
                        if (n !== null) val = n.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + "€";
                      }
                    } else if (isPvpCol(col.key) && isOnRequestVal(val)) {
                      // UL's plain "PVP" (no € in header, so not col.isNumeric) — same rule, no currency formatting.
                      val = ON_REQUEST;
                    }
                    return (
                      <RootRefCell key={ci} ci={ci} col={col} val={val} isCallCell={isCallCell} columns={columns} onCellClick={onCellClick} />
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Blocked screen ────────────────────────────────────────────────────────────
function BlockedScreen() {
  return (
    <div className="pl-blocked">
      <div>
        <div className="pl-blocked__icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(224,92,92,0.7)" strokeWidth="1.5">
            <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
          </svg>
        </div>
        <div className="pl-blocked__title">Access restricted</div>
        <div className="pl-blocked__sub">Please use your personalised link to access the price list.</div>
      </div>
    </div>
  );
}


// ─── Tier Panel (collapsible, gear icon) ─────────────────────────────────────
function TierPanel({ tierOpts, activeTier, setTier, isActive, visible, activeTierLabel }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const activeColor = tierOpts.find(o => !o.group && isActive(o.t))?.color ?? "var(--pl-text-btn)";

  if (!visible) return null;
  return (
    <div className="pl-tier-panel-wrap" ref={wrapRef}>
      <button className="pl-tier-panel__gear pl-theme-btn" onClick={() => setOpen(o => !o)} title="Select Price List">
        <span className="pl-tier-btn__dot" style={{ "--tier-color": activeColor }} />
        {activeTierLabel ?? "Preview Tier"}
      </button>
      {open && (
        <div className="pl-tier-panel pl-tier-panel--open">
          <div className="pl-tier-panel__header">
            <div className="pl-tier-panel__dot" />
            <span className="pl-tier-panel__title">Preview Tier</span>
          </div>
          <div className="pl-tier-panel__list">
            {tierOpts.map((opt, i) => {
              if (opt.group) return (
                <div key={opt.group + i} className="pl-tier-group">{opt.group}</div>
              );
              const { label, sub, t, color } = opt;
              const active = isActive(t);
              return (
                <button key={label} className={`pl-tier-btn${active ? " pl-tier-btn--active" : ""}`}
                  onClick={() => { setTier(t); setOpen(false); }} style={{ "--tier-color": color }}>
                  <div className="pl-tier-btn__dot" />
                  <span className="pl-tier-btn__label">{label}</span>
                  <span className="pl-tier-btn__sub">{sub}</span>
                  {active && <span className="pl-tier-btn__active-dot">●</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main App ──────────────────────────────────────────────────────────────────
function AppInner() {
  const [themeName, setThemeName] = useState(() => loadThemeCookie());
  function toggleTheme() { const n = themeName === 'dark' ? 'light' : 'dark'; setThemeName(n); saveThemeCookie(n); }
  const [rawData,    setRawData]    = useState([]);
  const [columns,    setColumns]    = useState([]);
  const [filter,     setFilter]     = useState("");
  const [inputValue,  setInputValue] = useState("");
  const [sortCol,    setSortCol]    = useState(null);
  const [sortDir,    setSortDir]    = useState("asc");
  const [typeFilter,  setTypeFilter] = useState({ ACC: true, PRODUCT: true });
  const [colFilters,  setColFilters]  = useState({});
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const pendingRefFilter = useRef(null);

  const { slug } = useParams();
  const urlTier = useMemo(() => detectTierFromSlug(slug), [slug]);
  const isInternal = urlTier?.internal === true;

  const [tierOverride, setTO] = useState(() => loadTierCookie());
  const activeTier = isInternal ? (tierOverride ?? CALL_TIER) : urlTier ?? CALL_TIER;
  function setTier(t) {
    setTO(t);
    saveTierCookie(t);
    const newBrand = t?.brand ?? "bpm";
    const curBrand = (tierOverride?.brand) ?? "bpm";
    setIsTransitioning(true);
    setReloadKey(k => k + 1);
    setRawData([]);
    setColumns([]);
    setFilter("");
    setInputValue("");
    setColFilters({});
    if (newBrand !== curBrand) {
      // non-BPM brands only have full list; BPM restores last used list from cookie
      setSelectedList(newBrand === "bpm" ? (loadListCookie() ?? "reduced") : "full");
    }
  }

  // selectedList: direct tiers always "full"; internal uses cookie (default "reduced"); others "full"
  const [selectedList, setSelectedList] = useState(() => {
    if (!urlTier) return null;
    if (urlTier.direct) return "full";
    if (urlTier.internal) return loadListCookie() ?? "reduced";
    return "full";
  });

  useEffect(() => {
    document.body.className = themeName;
  }, [themeName]);

  // Determine which precomputed tier file to load (already priced — no
  // multiplier math happens in this browser). Brand + selected list decide
  // the "list" (full/reduced); the tier itself decides the "tierKey". When no
  // real tier is active yet (CALL_TIER, internal user who hasn't picked one),
  // fall back to the active brand's base tier just to populate the table
  // (every numeric cell still renders as "Get Quote", see VirtualList).
  const activeBrand   = (isInternal ? tierOverride?.brand : activeTier?.brand) ?? "bpm";
  const tierKeyToLoad = activeTier?.key ?? tiersConfig.baseTierByBrand[activeBrand];
  const listToLoad    = useMemo(() => {
    if (!selectedList) return null;
    if (activeTier?.direct) return "full";
    return selectedList === "full" ? "full" : "reduced";
  }, [selectedList, activeTier]);

  function setColWidth(key, w) {
    setColumns(prev => prev.map(c => c.key === key ? { ...c, width: Math.max(60, w) } : c));
  }

  function inferColumns(headers, sample) {
    return headers.map(h => {
      // Price column = header contains €. Everything else left untouched.
      const isNumeric = h.includes("€");
      const vals      = sample.slice(0, 40).map(r => r[h]).filter(v => v != null && v !== "");
      const maxValLen = vals.reduce((mx, v) => Math.max(mx, String(v).length), 0);
      // Reserve room for "ON REQUEST" on the PVP column even if no sampled row needs it yet.
      const reserve   = isPvpCol(h) ? ON_REQUEST.length : 0;
      const maxLen    = Math.max(h.length, maxValLen, reserve);
      const width     = Math.min(400, Math.max(60, Math.ceil(maxLen * (isNumeric ? 9 : 8.5)) + 32));
      const label = isNumeric ? h.replace(/\s*€\s*/g, '').trim() : h;
      return { key: h, label, isNumeric, width };
    });
  }



  function onData(json) {
    if (!json.length) return;
    const headers = Object.keys(json[0]);
    const cols = inferColumns(headers, json);
    setColumns(cols);
    setRawData(json.map((r, i) => ({ ...r, __id: i })));
    setFilter("");
    // Default sort: alphabetical by product name, until the user picks a column.
    const nameCol = headers.find(h => h.trim().toUpperCase() === "PRODUCT NAME");
    setSortCol(nameCol ?? null);
    setSortDir("asc");
    setIsTransitioning(false);
    // Apply pending reference filter on the correct REFERENCE column key
    if (pendingRefFilter.current) {
      const ref = pendingRefFilter.current;
      pendingRefFilter.current = null;
      const refCol = headers.find(h => isSearchFilter(h));
      if (refCol) {
        setColFilters({ [refCol]: new Set([ref]) });
      }
    }
  }

  // Debounce input → filter (300ms so typing feels instant)
  useEffect(() => {
    const t = setTimeout(() => setFilter(inputValue), 300);
    return () => clearTimeout(t);
  }, [inputValue]);

  function switchList(type) {
    if (type !== null) setIsTransitioning(true);
    if (type && isInternal) saveListCookie(type);
    setSelectedList(type);
    setRawData([]);
    setColumns([]);
    setFilter("");
    setInputValue("");
    setColFilters({});
  }

  const { status: loadStatus, errorKind, refresh: refreshFile, generatedAt: rawGeneratedAt } = useAutoLoad(tierKeyToLoad, listToLoad, onData, reloadKey);
  // Persist lastModified so it shows even before a file is loaded
  const [lastModified, setLastModifiedStored] = useState(() => localStorage.getItem("pb_lastmod") || null);
  useEffect(() => {
    if (rawGeneratedAt) {
      setLastModifiedStored(rawGeneratedAt);
      localStorage.setItem("pb_lastmod", rawGeneratedAt);
    }
  }, [rawGeneratedAt]);

  // Columns visible (hide TYPE)
  const visibleColumns = useMemo(() => columns.filter(c => c.key.toUpperCase() !== "TYPE"), [columns]);

  const [processedData, setProcessedData] = useState([]);
  const [isFiltering,   setIsFiltering]   = useState(false);

  useEffect(() => {
    if (!rawData.length) { setProcessedData([]); setIsFiltering(false); return; }

    // Show spinner first, then process in next macro-task
    setIsFiltering(true);
    let cancelled = false;

    const run = async () => {
      // Yield to browser so spinner paints
      await new Promise(r => setTimeout(r, 0));
      if (cancelled) return;

      const activeTypes = Object.entries(typeFilter).filter(([,v]) => v).map(([k]) => k);
      const q = filter.trim().toLowerCase();
      const CHUNK = 5000;
      let result = [];

      // Process in chunks to avoid blocking
      for (let i = 0; i < rawData.length; i += CHUNK) {
        if (cancelled) return;
        const chunk = rawData.slice(i, i + CHUNK);
        for (const row of chunk) {
          // TYPE filter
          if (activeTypes.length < 2) {
            const val = String(row["TYPE"] ?? row["type"] ?? row["Type"] ?? "").toUpperCase();
            if (!activeTypes.some(t => val === t.toUpperCase())) continue;
          }
          // Text filter
          if (q) {
            const match = visibleColumns.some(col => String(row[col.key] ?? "").toLowerCase().includes(q));
            if (!match) continue;
          }
          // Column value filters
          let passCol = true;
          for (const [key, allowed] of Object.entries(colFilters)) {
            if (!allowed) continue;
            const val = String(row[key] ?? "").toLowerCase();
            if (isSearchFilter(key)) {
              // substring match for REFERENCE columns
              const term = [...allowed][0]?.toLowerCase() ?? "";
              if (term && !val.includes(term)) { passCol = false; break; }
            } else {
              if (!allowed.has(String(row[key] ?? ""))) { passCol = false; break; }
            }
          }
          if (!passCol) continue;
          result.push(row);
        }
        // Yield between chunks so browser stays responsive
        if (i + CHUNK < rawData.length) {
          await new Promise(r => setTimeout(r, 0));
        }
      }

      if (cancelled) return;

      // Sort
      if (sortCol) {
        const col = columns.find(c => c.key === sortCol);
        result.sort((a, b) => {
          let va = a[sortCol], vb = b[sortCol];
          if (col?.isNumeric) { va = parseNum(va) ?? 0; vb = parseNum(vb) ?? 0; }
          else { va = String(va ?? "").toLowerCase(); vb = String(vb ?? "").toLowerCase(); }
          return (va < vb ? -1 : va > vb ? 1 : 0) * (sortDir === "asc" ? 1 : -1);
        });
      }

      setProcessedData(result);
      setIsFiltering(false);
    };

    run();
    return () => { cancelled = true; };
  }, [rawData, visibleColumns, filter, sortCol, sortDir, typeFilter, colFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSort(key) {
    if (sortCol === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(key); setSortDir("asc"); }
  }

  // Values coming from processedData are already this tier's final price
  // (computed at build time) — export just writes them out, no math here.
  async function downloadExcel() {
    const XLSXmod = await import("xlsx");
    const XLSX = XLSXmod.default ?? XLSXmod;
    const exportCols = visibleColumns.filter(c => c.key !== "__id");
    const data = processedData.map(row => {
      const obj = {};
      exportCols.forEach(col => {
        let val = row[col.key];
        if (isPvpCol(col.key) && isOnRequestVal(val)) {
          val = ON_REQUEST;
        } else if (col.isNumeric && val !== "" && val != null) {
          const n = parseNum(val);
          if (n !== null) val = n;
        }
        obj[col.label] = val;
      });
      return obj;
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), "Price List");
    XLSX.writeFile(wb, `pricelist-${(activeTier.key ?? "base").replace(/^2026-/, "")}.xlsx`);
  }

  const totalW = visibleColumns.reduce((s, c) => s + c.width, 0);

  // Tier switcher options — sub-label is derived from the real multiplier so
  // it can't drift out of sync with the actual price (as the old hardcoded
  // "+20%" text did before Tarifa 3 was corrected to 15%).
  function tierSub(t) { return t.multiplier === 1 ? "Base" : `+${Math.round((t.multiplier - 1) * 100)}%`; }
  const tierOpts = [
    { group: "BPM" },
    { label: "BPM T1 Pricelist", sub: tierSub(CONFIG.tiers["2026-bpmwork"]),                   t: CONFIG.tiers["2026-bpmwork"],                   color: "var(--pl-text-btn)" },
    { label: "BPM T2 Pricelist", sub: tierSub(CONFIG.tiers["2026-row-bpmwork"]),               t: CONFIG.tiers["2026-row-bpmwork"],               color: "rgba(200,168,75,0.8)" },
    { label: "BPM T3 Pricelist", sub: tierSub(CONFIG.tiers["2026-rowp-bpmwork"]),              t: CONFIG.tiers["2026-rowp-bpmwork"],              color: "rgba(224,120,92,0.8)" },
    { group: "KOHL" },
    { label: "KOHL T1 Pricelist",sub: tierSub(CONFIG.tiers["2026-kohl-lightislife"]),          t: CONFIG.tiers["2026-kohl-lightislife"],          color: "var(--pl-text-btn)" },
    { label: "KOHL T2 Pricelist",sub: tierSub(CONFIG.tiers["2026-row-kohl-lightislife"]),      t: CONFIG.tiers["2026-row-kohl-lightislife"],      color: "rgba(200,168,75,0.8)" },
    { label: "KOHL T3 Pricelist",sub: tierSub(CONFIG.tiers["2026-rowp-kohl-lightislife"]),     t: CONFIG.tiers["2026-rowp-kohl-lightislife"],     color: "rgba(224,120,92,0.8)" },
    { group: "UL" },
    { label: "BPM USA Pricelist",sub: "USA",                                                   t: CONFIG.tiers["2026-ulproducts"],                color: "rgba(120,200,180,0.8)" },
  ];

  // Find active tier label for the button
  const activeTierLabel = tierOpts.find(o => !o.group && isActive(o.t))?.label ?? "Preview Tier";

  function isActive(t) {
    return !!activeTier?.callOnly === !!t?.callOnly && activeTier?.key === t?.key && activeTier?.multiplier === t?.multiplier;
  }

  if (!urlTier) return <BlockedScreen />;

  return (
    <>

      <div className="pl-root">

        {/* ── Header ── */}
        <header className="pl-glass pl-header">
          {/* Top glow line */}
          <div className="pl-header__top-line" />

          <div className="pl-header__logo-wrap">
            <img
              src={`${process.env.PUBLIC_URL}/assets/${activeBrand === "kohl" ? "kohl-logo.png" : "logo.png"}`}
              alt="BPM Lighting"
              className="pl-header__logo"
            />
          </div>

          <div className="pl-header__right">
            {isInternal && selectedList && (
              <TierPanel tierOpts={tierOpts} activeTier={activeTier} setTier={setTier} isActive={isActive} visible activeTierLabel={activeTierLabel} />
            )}
            <ThemeToggle theme={themeName} onToggle={toggleTheme} />
            <div className="pl-header__title-wrap">
              <div className="pl-header__title">Price List <span className="pl-header__title-year">2026</span></div>
              <div className="pl-header__subtitle">last update · <span className="pl-header__subtitle-date">{lastModified ? new Date(lastModified).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : CONFIG.lastUpdate}</span></div>
            </div>
          </div>
        </header>

        {/* ── Toolbar ── */}
        <div className="pl-glass pl-toolbar">

          {/* Search */}
          <div className="pl-search">
            <svg className="pl-search__icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input value={inputValue} onChange={e => setInputValue(e.target.value)}
              placeholder={selectedList ? "Filter products, references, categories…" : "Load a catalogue first…"}
              disabled={!selectedList}
              className="pl-search__input" />
            {inputValue && <span onClick={() => { setInputValue(""); setFilter(""); }} className="pl-search__clear">×</span>}
          </div>

          {/* TYPE filter buttons */}
          {rawData.length > 0 && (
            <div className="pl-type-btns">
              {["ACC", "PRODUCT"].map(type => {
                const active = typeFilter[type];
                const bothActive = typeFilter.ACC && typeFilter.PRODUCT;
                const isLastActive = active && !bothActive;
                return (
                  <TypeBtn key={type} label={type} active={active} isLastActive={isLastActive}
                    onClick={() => { if (!isLastActive) setTypeFilter(prev => ({ ...prev, [type]: !prev[type] })); }} />
                );
              })}
            </div>
          )}

          {/* Row count + reset */}
          {rawData.length > 0 && (() => {
            const hasFilters = inputValue || !typeFilter.ACC || !typeFilter.PRODUCT || Object.values(colFilters).some(Boolean) || sortCol;
            return (
              <div className="pl-counter">
                {isFiltering
                  ? <><div className="pl-counter__spinner" /><span className="pl-counter__total">Filtering…</span></>
                  : <><span className="pl-counter__value">{processedData.length.toLocaleString("es-ES")}</span><span className="pl-counter__total"> / {rawData.length.toLocaleString("es-ES")}</span></>
                }
                {hasFilters && !isFiltering && (
                  <span onClick={() => { setInputValue(""); setFilter(""); setTypeFilter({ ACC: true, PRODUCT: true }); setColFilters({}); setSortCol(null); }}
                    title="Reset all filters"
                    className="pl-counter__reset">
                    ⊗
                  </span>
                )}
              </div>
            );
          })()}

          <div className="pl-spacer" />

          {/* File status — loading/error only, done moves to footer */}
          {(loadStatus === "loading" || loadStatus === "error") && (
            <div className="pl-load-pill">
              {loadStatus === "loading" && <>
                <div className="pl-load-pill__dot" />
                <span className="pl-load-pill__text">Loading…</span>
              </>}
              {loadStatus === "error" && <>
                <div className="pl-load-pill__dot pl-load-pill__dot--error" />
                <span className="pl-load-pill__text pl-load-pill__text--error">
                  {errorKind === "notfound" ? "File not found" : "Connection problem"}
                </span>
              </>}
            </div>
          )}

          {/* Switch list button — only for BPM brand which has two lists */}
          {rawData.length > 0 && !activeTier?.direct && (
            <GlassBtn dot isSwitch
              onClick={() => switchList(selectedList === "full" ? "reduced" : "full")}>
              Switch to {selectedList === "full" ? "Quick View" : "Full Catalogue"}
            </GlassBtn>
          )}

          {/* Download */}
          {rawData.length > 0 && !activeTier.callOnly && (
            <GlassBtn onClick={downloadExcel} dot>
              Download Summary
              {filter && <span className="pl-glass-btn__sub">(filtered)</span>}
            </GlassBtn>
          )}
        </div>

        {/* ── Content ── */}
        <div className="pl-content">
          {rawData.length === 0 || isTransitioning ? (
            <div className="pl-center-screen">

              {/* ── Loading ── */}
              {(loadStatus === "loading" || isTransitioning) && (
                <div className="pl-loading">
                  <div className="pl-loading__icon">
                    <div className="pl-loading__spinner" />
                  </div>
                  <div className="pl-loading__text">Loading catalogue…</div>
                </div>
              )}

              {/* ── Error ── */}
              {loadStatus === "error" && !isTransitioning && (
                <div className="pl-loading fade-in">
                  <div className="pl-error__icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(224,92,92,0.7)" strokeWidth="1.5">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                  </div>
                  <div className="pl-error__title">
                    {errorKind === "notfound" ? "File not found" : "Connection problem — check your network"}
                  </div>
                  <button onClick={refreshFile} className="pl-error__back">Retry</button>
                </div>
              )}
            </div>
          ) : (
            <div className="pl-table-wrap">
              <VirtualList
                rows={processedData}
                allRows={rawData}
                filteredRows={processedData}
                columns={visibleColumns}
                totalWidth={Math.max(totalW, 800)}
                onSort={handleSort}
                sortCol={sortCol}
                sortDir={sortDir}
                callOnly={!!activeTier.callOnly}
                onResize={setColWidth}
                colFilters={colFilters}
                onColFilter={(key, val) => setColFilters(prev => ({ ...prev, [key]: val }))}
                onCellClick={selectedList === "reduced" ? (refVal) => {
                  if (!refVal) return;
                  pendingRefFilter.current = String(refVal);
                  switchList("full");
                } : undefined}
              />
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        {rawData.length > 0 && (
          <div className="pl-glass pl-footer">
            <span className="pl-footer__brand">
              BPM Lighting · <span className="pl-footer__team">BPM MKT TEAM</span>
            </span>
            {/* Cache status */}
            <div className="pl-footer__status">
              {loadStatus === "done" && (<>
                <div className="pl-footer__dot" />
                <span className="pl-footer__label">File loaded</span>
                <span title="Force reload from server" onClick={refreshFile} className="pl-footer__refresh">↺</span>
              </>)}
            </div>
          </div>
        )}


            
      </div>
    </>
  );
}

function RestrictedScreen() {
  return (
    <div className="pl-blocked">
      <div>
        <div className="pl-blocked__icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(255,0,138,0.7)" strokeWidth="1.5">
            <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
          </svg>
        </div>
        <div className="pl-blocked__title">Restricted Area</div>
        <div className="pl-blocked__sub">Please use your personalised link to access the price list.</div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter basename="/pricelist">
      <Routes>
        <Route path="/:slug" element={<AppInner />} />
        <Route path="/" element={<RestrictedScreen />} />
      </Routes>
    </BrowserRouter>
  );
}
